<template>
  <main class="page">
    <section class="page__intro">
        <h1 class="page__title">Dina försäkringar</h1>
        <p>Här ser du dina aktiva försäkringar och vad de innebär ifall du behöver använda dem.</p>
    </section>

    <LoadingSpinner v-if="isLoading"/>
    <section class="page__main insurances__main" v-else>
      <IntroWithInfo
        :title="healthInsurance.title"
        :intro="healthInsurance.intro"
        :info="healthInsurance.info"
      />

      <div class="insurances__cups">
        <Cup :chartData="cupData.cup1" :wrongSalary="wrongSalary" />
        <Cup :chartData="cupData.cup2" />
        <Cup :chartData="cupData.cup3" />
      </div>

      <InfoBoxSalary :wrongSalary="wrongSalary" :salaries="salaries" />

      <StackedColumns :graphData="graphData" />

      <IntroWithInfo
        :title="premiebefrielse.title"
        :intro="premiebefrielse.intro"
        :info="premiebefrielse.info"
      />

      <ConfirmationAndInfoBox :text="premiebefrielse.box" />

      <IntroWithInfo
        :title="efterlevnadsskydd.title"
        :intro="efterlevnadsskydd.intro"
        :info="efterlevnadsskydd.info"
      />

      <ConfirmationAndInfoBox :text="efterlevnadsskydd.box" />
    </section>
  </main>
</template>

<script>
import { LoadingSpinner } from '../components';
import IntroWithInfo from '../components/elements/Info/IntroWithInfo.vue';
import InfoBoxSalary from '../components/elements/Info/InfoBoxSalary.vue';
import ConfirmationAndInfoBox from '../components/elements/Info/ConfirmationAndInfoBox.vue';
import Cup from '../components/elements/charts/Cup.vue';
import StackedColumns from '../components/elements/charts/StackedColumns.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'Insurances',
    components: { LoadingSpinner, IntroWithInfo, Cup, InfoBoxSalary, StackedColumns, ConfirmationAndInfoBox },
    data() {
      return {
        healthInsurance: {
          title: "Sjukförsäkring",
          intro: "Ifall du blir sjuk vill du såklart ha så bra ersättning som möjligt till du blir frisk. Därför är det bra att ha en kompletterande försäkring via din pensionslösning då försäkringskassan enbart ersätter en del av din lön upp till vissa nivåer.",
          info: {
            title: "Information om sjukförsäkring",
            text: "Det finns olika ersättningar som hjälper dig ifall du skulle bli sjuk som kan ge dig upp till 90% av din lön kombinerat. Du kan vara sjukskriven från 25 till 100% av arbetstiden. Första dagen skall du sjukanmäla dig till din arbetsgivare för att sedan senaste dag 8 gå till läkaren för ett läkarintyg som du ger till din arbetsgivare. Ifall du varit sjuk i mer än 14 dagar skall du bli sjukanmäld till Försäkringskassan av din arbetsgivare. Efter det kan du ansöka om sjukpenning hos Försäkringskassan med läkarintyget. Ifall du skulle vara sjuk längre än 90 dagar så anmäler din arbetsgivare det till försäkringsbolag."
          },
        },
      }
    },
    created () {
      if(!this.insurances.status) { // Happens only once
        this.getInsurances()
      }
    },
    methods: {
      ...mapActions([ 'getInsurances', 'getFinancialSummary' ]),
      roundUpValuesInArray (array) {
        let roundedValues = []

        for(let i = 0; i < array.length; i++) {
          roundedValues.push(Math.round(array[i]))
        }

        return roundedValues
      }
    },
    computed: {
      ...mapGetters(['user', 'insurances', 'financialSummary']),
      isLoading: function () {
        return this.insurances.status === 'loading'
      },
      cupData: function () {
        let cupData = this.insurances.ins.sjukforsakringar.cupData

        return {
          cup1: {
            lowValue: 0,
            highValue: cupData.cup1.percent * 1000,
            content: {
              text: 'Din lön idag',
              values: cupData.cup1.value,
              base: true,
              linkable: true,
            }
          },
          cup2: {
            lowValue: 1000 - Math.round(cupData.cup2.percent * 100) * 10,
            highValue: Math.round(cupData.cup2.percent * 100) * 10,
            content: {
              text: 'Ifall du blir sjuk',
              values: this.roundUpValuesInArray(cupData.cup2.value),
            }
          },
          cup3: {
            lowValue: 1000 - Math.round(cupData.cup3.percent * 100) * 10,
            highValue: Math.round(cupData.cup3.percent * 100) * 10,
            content: {
              text: 'Ifall du blir långvarigt sjuk',
              values: this.roundUpValuesInArray(cupData.cup3.value),
            }
          },
        }
      },
      graphData: function () {
        let graphData = this.insurances.ins.sjukforsakringar.graphData
        let fixedGraphData = []
        let salary = graphData[0].salary

        for(let i = 0; i < graphData.length; i++) {
          fixedGraphData.push(
            {
              "category": graphData[i].label,
              "salary": graphData[i].salary ? graphData[i].salary : 'null',
              "fsk": graphData[i].fsk,
              "sju": graphData[i].sju,
              "no-income":  graphData[i].compareToSalary < 1 ? salary - graphData[i].total : 'null',
            }
          )
        }

        return fixedGraphData
      },
      premiebefrielse: function () {
        return {
          title: "Premiebefrielse",
          intro: "Ifall du blir sjuk längre än 90 dagar så går premiebefrielsen in och betalar delar eller hela din pensionspremie så länge du är sjuk istället för din arbetsgivare.",
          info: {
            title: "Information om premiebefrielse",
            text: "Du kan ha en premiebefrielseförsäkring som täcker inbetalningar till din tjänstepension när du är sjukskriven och kan vara bra ifall man blir sjuk längre och inte kan fortsätta sitt yrkesliv. Försäkringskassan informerar försäkringsbolaget och det krävs inget från din sida."
          },
          box: {
            no: "Vi kan se att du inte har en premiebefrielse.",
            yes: "Toppen! Vi ser att du har en premiebefrielse.",
            info: {
              title: "Hur skaffar jag premiebefrielse?",
              text: "Premieförsäkring ingår normalt i din ITP ifall ni har kollektivavtal, i annart fall måste du be din arbetsgivare att skaffa det via din tjänstepension ifall du vill vara försäkrad."
            }
          },
          has: this.insurances.ins.details.hasPremiebefrielse
        }
      },
      efterlevnadsskydd: function () {
        return {
          title: "Efterlevnadsskydd",
          intro: "Skyddet ger dina utvalda anhöriga ersättning ifall du skulle avlida innan en viss ålder. Detta val påverkar ditt pensionsbelopp som kommer betalas ut.",
          info: {
            title: "Information om efterlevnadsskydd",
            text: "De flesta som väljer att skaffa en efterlevandeskydd gör det när de har yngre barn som inte är myndiga som man delvis eller helvis försörjer eller ifall man har en partner som man vill skall klara av ett fortsatt bolån eller liknande. Detta är ett alternativ/komplement till en livförsäkring. Skyddet innebär att du skall även fylla i en hälsoformulär."
          },
          box: {
            no: "Vi kan se att du inte har ett efterlevandeskydd i din tjänstepension/privata pensionsförsäkring.",
            yes: "Vi kan se att du har ett efterlevandeskydd i din tjänstepension/privata pensionsförsäkring.",
            info: {
              title: "Hur skaffar jag efterlevnadsskydd?",
              text: "Efterlevandeskydd tecknas i samaband med tjänstepensionen eller när du satte upp din privata pensionförsäkring. Prata med din arbetsgivare eller privata pensionsbolag kring detta."
            },
            has: this.insurances.ins.overview.hasEfterlevnadsskydd,
          }
        }
      },
      wrongSalary: function () {
        let fskSalary = this.insurances.ins.overview.yearlySalary.amount / 12
        let salary = this.user.access.income.monthlySalary

        let wrongSalary = false

        if(fskSalary != salary) {
          wrongSalary = true
        }

        return wrongSalary
      },
      salaries: function () {
        return {
          fskSalary: this.insurances.ins.overview.yearlySalary.amount / 12,
          salary: this.user.access.income.monthlySalary,
        }
      }
    }
}
</script>

<style lang="scss" scoped>
.page__title {
  font-family: "Roboto", sans-serif;
  color: $gray;
  font-weight: lighter;
  margin-bottom: 30px;
}
.insurances {
  &__main {
    h3:not(:first-of-type) {
      margin-top: 3rem;
    }
  }

  &__cups {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
  }
}
</style>